import * as React from "react";
import {useState} from "react";
import {Table, TableBody, TableCell, TableContainer, TableRow} from "@mui/material";
import {Duration} from "@/components/widgets";
import {useIntl, defineMessage} from "react-intl";
import {HeaderWithOrdering, GenericPaginator} from "@dvrt/graphql-widgets";
import {StateChange, AgentState} from "@/api";
import {FormattedAlert} from "@dvrt/graphql-widgets";
import {DateTime} from "luxon";
import {Bin} from "d3-array";
import {timeDay} from "d3-time";
import {Member} from "../members/types";
import {Call, QueueCall} from "./types";
import {sumCallDuration, averageCallSetupTime, download_excel_agent_table} from "./tools";
import {SegmentType} from "@/api";
import FormattedButton from "@/components/FormattedButton";

const loadingStyle: React.CSSProperties = {
    pointerEvents: "none",
    opacity: 0.4,
};

interface DailySummary {
    loggedIn: number;
    onThePhone: number;
    afterCallWork: number;
    busy: number;
    ccAux1: number;
    ccAux2: number;
    ccAux3: number;
    meeting: number;
    meal: number;
}

const nullDailySummary: DailySummary = {
    loggedIn: 0,
    onThePhone: 0,
    afterCallWork: 0,
    busy: 0,
    ccAux1: 0,
    ccAux2: 0,
    ccAux3: 0,
    meeting: 0,
    meal: 0,
};

interface CallSummary {
    // number of calls handled
    queueCalls: number;
    // agent spent in queue calls
    queueCallTime: number;

    avgSetupTime: number;
}

const nullCallSummary: CallSummary = {
    queueCalls: 0,
    queueCallTime: 0,
    avgSetupTime: 0,
};

export interface Entry {
    agent: string;
    day: Date;
    data: DailySummary;
    calls: CallSummary;
}

export const summarizeOneDay = (rawChanges: StateChange[]): DailySummary => {
    if (rawChanges === undefined || rawChanges.length == 0) {
        return structuredClone(nullDailySummary);
    }

    const changes = rawChanges.map((change) => ({
        time: new Date(change.timeOfChange),
        newState: change.state,
        oldState: change.oldState,
    }));

    changes.sort((a, b) => a.time.valueOf() - b.time.valueOf());

    const summary: DailySummary = structuredClone(nullDailySummary);

    let currentState = changes[0].oldState;
    let currentTime: Date = timeDay.floor(changes[0].time);

    for (const change of changes) {
        const newState = change.newState;
        const stateDuration = change.time.valueOf() - currentTime.valueOf();

        switch (currentState) {
            case AgentState.Available:
                summary.loggedIn += stateDuration;
                break;
            case AgentState.OnThePhone:
                summary.loggedIn += stateDuration;
                summary.onThePhone += stateDuration;
                break;
            case AgentState.AfterCallWork:
                summary.loggedIn += stateDuration;
                summary.afterCallWork += stateDuration;
                break;
            case AgentState.Busy:
                summary.loggedIn += stateDuration;
                summary.busy += stateDuration;
                break;
            case AgentState.CcAux1:
                summary.loggedIn += stateDuration;
                summary.ccAux1 += stateDuration;
                break;
            case AgentState.CcAux2:
                summary.loggedIn += stateDuration;
                summary.ccAux2 += stateDuration;
                break;
            case AgentState.CcAux3:
                summary.loggedIn += stateDuration;
                summary.ccAux3 += stateDuration;
                break;
            case AgentState.Meeting:
                summary.loggedIn += stateDuration;
                summary.meeting += stateDuration;
                break;
            case AgentState.Meal:
                summary.loggedIn += stateDuration;
                summary.meal += stateDuration;
                break;
            case AgentState.NotAvailable:
                break;
        }

        currentTime = change.time;
        currentState = newState;
    }

    return summary;
};

function summarizeCalls(calls: Call[]): CallSummary {
    if (!calls || calls.length == 0) {
        return nullCallSummary;
    }
    const summary: CallSummary = {...nullCallSummary};
    const handledCalls = calls.filter((c) => c.segmentType == SegmentType.Normal);
    summary.queueCalls = handledCalls.length;
    summary.queueCallTime = sumCallDuration(handledCalls);
    summary.avgSetupTime = averageCallSetupTime(calls.filter((c) => c.segmentType == SegmentType.Normal));
    return summary;
}

function summarize(
    binnedChanges: Bin<StateChange, Date>[],
    binnedCalls: Bin<QueueCall, Date>[],
    members: Member[],
): Entry[] {
    const entries: Entry[] = [];
    binnedChanges.forEach((bin, idx) => {
        // for (const bin of binnedChanges) {
        const day = bin.x0;
        for (const member of members) {
            entries.push({
                agent: `${member.number.extension.displayName} (${member.number.shortNumber})`,
                day: day,
                data: summarizeOneDay(bin.filter((ch) => ch.agentNumberId === member.numberId)),
                calls: summarizeCalls(
                    binnedCalls[idx]
                        .map((call) => {
                            return call.agent.find((ac) => ac.remoteNumberId == member.numberId);
                        })
                        .filter((call) => call !== undefined),
                ),
            });
        }
    });
    return entries;
}

type EntryCompare = (a: Entry, b: Entry) => number;
type ComparatorFactory = (isDesc: boolean) => EntryCompare;

const agentComparatorFactory = (isDesc: boolean) => (a: Entry, b: Entry) =>
    a.agent.localeCompare(b.agent) * (isDesc ? -1 : 1);

const dayComparatorFactory = (isDesc: boolean) => (a: Entry, b: Entry) =>
    (a.day.getTime() - b.day.getTime()) * (isDesc ? -1 : 1);

const dataComparatorFactory = (isDesc: boolean, field: keyof DailySummary) => (a: Entry, b: Entry) =>
    (a.data[field] - b.data[field]) * (isDesc ? -1 : 1);

const fieldTypes: {[K: string]: ComparatorFactory} = {
    agent: agentComparatorFactory,
    day: dayComparatorFactory,
};

const fieldComparatorFactory = (isDesc: boolean, field: string): EntryCompare => {
    if (field in fieldTypes) {
        return fieldTypes[field](isDesc);
    }

    return dataComparatorFactory(isDesc, field as keyof DailySummary);
};

function sortSummaries(entries: Entry[], orders: string[]) {
    if (entries.length === 0) return;

    const orderField = orders[0] ?? "day";
    const isDesc = orderField.startsWith("~");
    const fieldName = orderField.replace("~", "");

    const comparator = fieldComparatorFactory(isDesc, fieldName);

    entries.sort(comparator);
}

type Props = {
    states: Bin<StateChange, Date>[];
    calls: Bin<QueueCall, Date>[];
    members: Member[];
    loading: boolean;
    style?: React.CSSProperties;
    initialOrder?: string[];
};

export const AgentStateSummary = ({states, calls, members, loading, style, initialOrder}: Props) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [order, setOrder] = useState<string[]>(initialOrder || []);

    const entries = summarize(states, calls, members);
    const intl = useIntl();

    sortSummaries(entries, order);

    return (
        <div>
            <TableContainer style={style}>
                <Table size="small" style={{...(loading ? loadingStyle : {})}}>
                    <HeaderWithOrdering
                        fields={[
                            [
                                {
                                    path: "agent",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.agent",
                                        defaultMessage: "Agent",
                                    }),
                                    rowSpan: 2,
                                },
                                {
                                    path: "day",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.day",
                                        defaultMessage: "Day",
                                    }),
                                    rowSpan: 2,
                                },
                                {
                                    path: "loggedIn",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.loggedIn",
                                        defaultMessage: "Logged in",
                                    }),
                                },
                                {
                                    path: "afterCallWork",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.afterCallWork",
                                        defaultMessage: "ACW",
                                    }),
                                },
                                {
                                    path: "onThePhone",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.onThePhone",
                                        defaultMessage: "On the phone",
                                    }),
                                },
                                {
                                    path: "busy",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.busy",
                                        defaultMessage: "Busy",
                                    }),
                                },
                                {
                                    path: "ccAux1",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.ccAux1",
                                        defaultMessage: "AUX1",
                                    }),
                                },
                                {
                                    path: "ccAux2",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.ccAux2",
                                        defaultMessage: "AUX2",
                                    }),
                                },
                                {
                                    path: "ccAux3",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.ccAux3",
                                        defaultMessage: "AUX3",
                                    }),
                                },
                            ],
                            [
                                {
                                    path: "meeting",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.meeting",
                                        defaultMessage: "Meeting",
                                    }),
                                },
                                {
                                    path: "meal",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.meal",
                                        defaultMessage: "Meal",
                                    }),
                                },
                                {
                                    path: "calls",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.calls",
                                        defaultMessage: "Call #",
                                    }),
                                },
                                {
                                    path: "callTime",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.callTime",
                                        defaultMessage: "In call",
                                    }),
                                },
                                {
                                    path: "avgSetupTime",
                                    label: defineMessage({
                                        id: "call_queue.statistics.agent-report.fields.avgSetupTime",
                                        defaultMessage: "Setup time",
                                    }),
                                },
                            ],
                        ]}
                        multipleOrdering={false}
                        order={order}
                        orderableFields="all"
                        setOrder={setOrder}
                    />
                    <TableBody>
                        {entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((entry, idx) => (
                            <React.Fragment key={idx}>
                                <TableRow>
                                    <TableCell rowSpan={2}>{entry.agent}</TableCell>
                                    <TableCell rowSpan={2}>
                                        {DateTime.fromJSDate(entry.day).toLocaleString(DateTime.DATE_FULL)}
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.loggedIn / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.afterCallWork / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.onThePhone / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.busy / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.ccAux1 / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.ccAux2 / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.ccAux3 / 1000} />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Duration value={entry.data.meeting / 1000} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={entry.data.meal / 1000} />
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>{entry.calls.queueCalls}</TableCell>
                                    <TableCell>
                                        <Duration value={Math.round(entry.calls.queueCallTime)} />
                                    </TableCell>
                                    <TableCell>
                                        <Duration value={Math.round(entry.calls.avgSetupTime)} />
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                        {entries.length === 0 && (
                            <tr>
                                <td colSpan={100}>
                                    <FormattedAlert
                                        message={defineMessage({
                                            id: "call_list.empty",
                                            defaultMessage: "Could not find any records matching the criteria",
                                        })}
                                        severity="info"
                                        containerStyle={{margin: 15}}
                                    />
                                </td>
                            </tr>
                        )}
                    </TableBody>
                    <GenericPaginator
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={entries.length}
                    />
                </Table>
            </TableContainer>
            <FormattedButton
                message={defineMessage({id: "download_data", defaultMessage: "Download data"})}
                onClick={() => download_excel_agent_table(entries, intl)}
            />
        </div>
    );
};
